import styled from "styled-components";

import { ANIMATION, QUERIES, SPACING, WEIGHTS } from "../../../utils/constants";

export const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--color-background-0);
  color: var(--color-text-default);
`;

export const TopBar = styled.header`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${SPACING.md}px ${SPACING.xl}px;
    border-bottom: 1px solid var(--color-border);
  }
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
`;

export const BrandMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: ${SPACING.sm}px;
  background: var(--color-primary);
  color: var(--color-white);
  font-weight: ${WEIGHTS.bold};
  font-size: var(--text-md);
`;

export const BrandName = styled.span`
  font-weight: ${WEIGHTS.medium};
  font-size: var(--text-md);
  letter-spacing: -0.01em;
`;

export const Main = styled.main`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: ${SPACING.lg}px ${SPACING.md}px;
  /* Clear the fixed action bar on small screens. */
  padding-bottom: ${SPACING.xxl * 3 + SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    align-items: center;
    padding: ${SPACING.xxl}px ${SPACING.lg}px;
  }
`;

export const Card = styled.section`
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;

  @media ${QUERIES.tabletLargeAndUp} {
    background: var(--color-background-2);
    border: 1px solid var(--color-border);
    border-radius: 20px;
    box-shadow: var(--shadow-xlarge);
    padding: ${SPACING.xl}px;
  }
`;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    align-items: center;
    text-align: center;
  }
`;

export const Eyebrow = styled.span`
  font-size: var(--text-xs);
  font-weight: ${WEIGHTS.bold};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-primary);
`;

export const Title = styled.h1`
  margin: 0;
  font-size: var(--text-xxxl);
  font-weight: ${WEIGHTS.bold};
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: var(--color-text-default);
`;

export const Subtitle = styled.p`
  margin: 0;
  max-width: 44ch;
  font-size: var(--text-md);
  line-height: 1.6;
  color: var(--color-text-secondary);
`;

export const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  background: var(--color-background-2);
  box-shadow: var(--shadow-small);
`;

export const Avatar = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--color-primary);
  color: var(--color-white);
  font-weight: ${WEIGHTS.bold};
  font-size: var(--text-md);
  letter-spacing: 0.02em;
`;

export const IdentityInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
`;

export const IdentityName = styled.span`
  font-weight: ${WEIGHTS.medium};
  font-size: var(--text-md);
  color: var(--color-text-default);
`;

export const IdentityMeta = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const IdentityEmail = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ProviderGlyph = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  svg {
    display: block;
    width: 14px;
    height: 14px;
  }
`;

export const VerifiedBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: var(--color-success);
  background: var(--color-status-success-background-soft);

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Fieldset = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const FieldLegend = styled.label`
  font-size: var(--text-base);
  font-weight: ${WEIGHTS.medium};
  color: var(--color-text-default);
`;

export const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1.1fr;
  gap: ${SPACING.sm}px;
`;

export const DateColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const PartLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: ${WEIGHTS.medium};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

const controlStyles = `
  width: 100%;
  box-sizing: border-box;
  padding: ${SPACING.sm + SPACING.xs}px ${SPACING.sm + SPACING.xs}px;
  font: inherit;
  font-size: var(--text-md);
  color: var(--color-text-default);
  background: var(--color-background-2);
  border: 1px solid var(--color-border);
  border-radius: ${SPACING.sm}px;
  transition:
    border-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    box-shadow ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut};

  &::placeholder {
    color: var(--color-text-muted);
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const MonthSelect = styled.select<{ $hasError?: boolean }>`
  ${controlStyles}
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2375808a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${SPACING.sm + SPACING.xs}px center;
  padding-right: ${SPACING.xl}px;
  border-color: ${({ $hasError }) =>
    $hasError ? "var(--color-error)" : "var(--color-border)"};
`;

export const PartInput = styled.input<{ $hasError?: boolean }>`
  ${controlStyles}
  text-align: center;
  border-color: ${({ $hasError }) =>
    $hasError ? "var(--color-error)" : "var(--color-border)"};
`;

export const ErrorText = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-error);
`;

export const PrivacyNote = styled.p`
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm}px;
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-text-secondary);

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: ${SPACING.xxs}px;
    color: var(--color-text-muted);
  }
`;

export const PrivacyLink = styled.a`
  color: var(--color-primary);
  font-weight: ${WEIGHTS.medium};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndDown} {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: ${SPACING.md}px;
    padding-bottom: calc(${SPACING.md}px + env(safe-area-inset-bottom));
    background: var(--color-background-0);
    border-top: 1px solid var(--color-border);
    box-shadow: 0 -4px 20px var(--color-box-shadow);
  }
`;

export const Footer = styled.footer`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACING.md}px;
    padding: ${SPACING.md}px ${SPACING.xl}px;
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
  }
`;

export const FooterLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
`;

export const FooterLink = styled.a`
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut};

  &:hover {
    color: var(--color-primary);
  }
`;
